import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, Box, Divider, IconButton, Menu, MenuItem, Typography } from '@mui/material';

import {
  AlertCircle,
  ChevronRight,
  Coins,
  Copy,
  CreditCard,
  FileText,
  Flag,
  Globe,
  LifeBuoy,
  LogOut,
  Mail,
  MailCheck,
  User,
  UserCheck,
} from 'lucide-react';
import moment from 'moment';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { useSupportContactEnabled } from '../../hooks/useIdlc1129Features';
import { useSupportMailto } from '../../hooks/useSupportMailto.ts';
import { getInitials } from '../../services/util';
import { subscriptionReducer } from '../../store/reducers/subscription.reducer.ts';
import { userReducer } from '../../store/reducers/user.reducer';
import { getSubscription } from '../../store/selectors/subscription.selector.ts';
import { getProfilePictureUrl, getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';
import { green01, greyP5 } from '../../theme';
import { SUPPORT_EMAIL } from '../../utils/support.ts';

// Extract reducer actions
const { logout, loadProfilePicture, updateUser } = userReducer.actions;
const { loadSubscription } = subscriptionReducer.actions;

interface NavbarProfileProps {
  userContext: boolean;
}

export const NavbarProfile = ({ userContext }: NavbarProfileProps) => {
  const { t, i18n } = useTranslation();
  const supportMailto = useSupportMailto();
  const supportContact = useSupportContactEnabled();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const languages = [
    { code: 'en', label: t('common.en') },
    { code: 'de', label: t('common.de') },
    { code: 'it', label: t('common.it') },
    { code: 'ar', label: t('common.ar') },
  ];

  const adminPages = [
    {
      path: '/impersonate',
      label: t('navbar.impersonateUser'),
      icon: UserCheck,
    },
    {
      path: '/invitation',
      label: t('navbar.sendInvitation'),
      icon: Mail,
    },
    {
      path: '/credits',
      label: t('navbar.addUserCredits'),
      icon: Coins,
    },
    {
      path: '/copy-project',
      label: t('navbar.copyProject'),
      icon: Copy,
    },
    {
      path: '/feature-flags',
      label: t('navbar.featureFlags'),
      icon: Flag,
    },
    {
      path: '/subscriptions',
      label: t('navbar.userSubscriptions'),
      icon: CreditCard,
    },
    {
      path: '/email-subscriptions',
      label: t('navbar.emailSubscriptions'),
      icon: MailCheck,
    },
    {
      path: '/audit-logs',
      label: t('navbar.auditLogs', 'Audit Logs'),
      icon: FileText,
    },
  ];

  // Get user from Redux store
  const user = useAppSelector(getUser);
  const profilePictureUrl = useAppSelector(getProfilePictureUrl);
  const subscription = useAppSelector(getSubscription);

  // Load profile picture if user has a profilePictureId
  useEffect(() => {
    if (user?.profilePictureId) {
      dispatch(loadProfilePicture(user.profilePictureId));
    }
  }, [user?.profilePictureId, dispatch]);

  useEffect(() => {
    dispatch(loadSubscription());
  }, [dispatch]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setProfileDropdownOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setLangAnchorEl(null);
    setProfileDropdownOpen(false);
  };

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLangAnchorEl(null);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    dispatch(updateUser({ preferredLanguage: lng }));
    handleClose();
  };

  const handleLogout = () => {
    dispatch(logout());
    handleClose();
  };

  if (!userContext || !user) {
    return null;
  }

  const getCurrentLanguage = () => {
    return user?.settings?.preferredLanguage || i18n.language;
  };

  const getRenewalDate = () => {
    if (subscription?.tier === 'TRIAL') {
      return moment(subscription.startedAt).add(14, 'days').format('DD.MM.YYYY');
    }
    // A paid subscription renews (or ends) when its paid period does.
    if (subscription?.currentPeriodEnd) {
      return moment(subscription.currentPeriodEnd).format('DD.MM.YYYY');
    }
    if (!subscription?.grantDayOfMonth) return null;
    const now = moment();
    let renewalDate = moment().date(subscription.grantDayOfMonth);
    if (renewalDate.isSameOrBefore(now)) {
      renewalDate = renewalDate.add(1, 'month');
    }
    return renewalDate.format('DD.MM.YYYY');
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        aria-controls={profileDropdownOpen ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={profileDropdownOpen ? 'true' : undefined}
        sx={{
          p: 0,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <Avatar
          src={profilePictureUrl}
          sx={{
            width: 40,
            height: 40,
            bgcolor: green01,
            color: 'black',
            fontWeight: 'bold',
            '&:hover': {
              boxShadow: '0 0 0 2px rgba(179, 236, 73, 0.5)',
            },
          }}
        >
          {getInitials(user.fullName)}
        </Avatar>
      </IconButton>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={profileDropdownOpen}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              backgroundColor: greyP5,
              color: 'white',
              minWidth: 180,
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              '& .MuiMenuItem-root': {
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(179, 236, 73, 0.1)',
                },
              },
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {user.fullName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem' }}>
            {user.email}
          </Typography>
          {subscription && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CreditCard size={14} color={green01} />
                <Typography
                  variant="body2"
                  sx={{
                    color: green01,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {subscription.tier}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Coins size={14} color="white" />
                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 500 }}>
                  {t('navbar.credits')}: {user.balance}
                </Typography>
              </Box>

              {user.balance < 50 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1,
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                  }}
                >
                  <AlertCircle size={14} color="#FF9800" />
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#FF9800',
                      fontSize: '0.65rem',
                      fontWeight: 500,
                    }}
                  >
                    {t('navbar.lowBalanceWarning')}
                  </Typography>
                </Box>
              )}

              {getRenewalDate() && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.4)',
                    fontSize: '0.65rem',
                    mt: 1,
                    pl: 3, // Align with text after icons
                  }}
                >
                  {subscription?.tier === 'TRIAL' || subscription?.cancelAtPeriodEnd
                    ? t('navbar.expiresOn')
                    : t('navbar.renewingOn')}
                  : {getRenewalDate()}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        <MenuItem
          onClick={() => {
            handleClose();
            history.push('/profile');
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <User size={20} />
          {t('navbar.profile')}
        </MenuItem>

        <MenuItem
          onClick={handleLanguageClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Globe size={20} />
            {t('navbar.language')}
          </Box>
          <ChevronRight size={16} />
        </MenuItem>

        <Menu
          anchorEl={langAnchorEl}
          open={Boolean(langAnchorEl)}
          onClose={handleLanguageClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: greyP5,
                color: 'white',
                minWidth: 150,
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                '& .MuiMenuItem-root': {
                  py: 1,
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(179, 236, 73, 0.1)',
                  },
                },
              },
            },
          }}
        >
          {languages.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              sx={{
                fontWeight: getCurrentLanguage() === lang.code ? 600 : 400,
                color: getCurrentLanguage() === lang.code ? '#B3EC49' : 'white',
              }}
            >
              {lang.label}
            </MenuItem>
          ))}
        </Menu>

        {supportContact && (
          <MenuItem
            onClick={() => {
              handleClose();
              window.location.href = supportMailto();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <LifeBuoy size={20} />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              {t('navbar.contactSupport')}
              <Typography component="span" sx={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.45)' }}>
                {SUPPORT_EMAIL}
              </Typography>
            </Box>
          </MenuItem>
        )}

        {user?.roles.includes('ADMIN') && [
          <Divider key="admin-top-divider" sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />,
          ...adminPages.map((adminPage) => {
            const IconComponent = adminPage.icon;
            return (
              <MenuItem
                key={adminPage.path}
                onClick={() => {
                  handleClose();
                  history.push(adminPage.path);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <IconComponent size={20} />
                {adminPage.label}
              </MenuItem>
            );
          }),
          <Divider key="admin-bottom-divider" sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />,
        ]}

        <MenuItem
          onClick={handleLogout}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <LogOut size={20} />
          {t('navbar.logout')}
        </MenuItem>
      </Menu>
    </>
  );
};
